import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, Trash2, RefreshCw, Power, Server, 
  Wifi, Clock, CheckCircle, XCircle, Edit2, Save, X, Eye, EyeOff, Copy, Terminal,
  Network, Route, Globe, ArrowRight, Layers, Router
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import POPManagement from './POPManagement';

const L2TPManagement = () => {
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [serverStatus, setServerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Route management states
  const [routes, setRoutes] = useState([]);
  const [interfaces, setInterfaces] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [showAddRouteModal, setShowAddRouteModal] = useState(false);
  const [routeType, setRouteType] = useState('static'); // 'static' or 'multipath'
  const [newRoute, setNewRoute] = useState({
    destination: '',
    gateway: '',
    interface: '',
    metric: '',
    persistent: true
  });
  const [multipathNexthops, setMultipathNexthops] = useState([
    { dev: '', weight: 1 },
    { dev: '', weight: 1 }
  ]);
  const [routers, setRouters] = useState([]);
  const [showPasswords, setShowPasswords] = useState({});
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Form states
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    ip_address: '*',
    description: '',
    router_id: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Toggle password visibility for a specific user
  const togglePassword = (username) => {
    setShowPasswords(prev => ({
      ...prev,
      [username]: !prev[username]
    }));
  };

  // Format bytes to human readable format
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Get session info for a user (kept for compatibility)
  const getSessionInfo = (username) => {
    return sessions.find(s => s.username === username);
  };

  // Script generation moved to POP Management tab







  useEffect(() => {
    fetchData();
    fetchRouters();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchUsers(),
      fetchSessions(),
      fetchServerStatus()
    ]);
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/l2tp/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Backend returns 'users' not 'data'
          setUsers(data.users || data.data || []);
        } else {
          setUsers([]);
        }
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/l2tp/sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchServerStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/l2tp/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServerStatus(data.data);
      }
    } catch (error) {
      console.error('Error fetching server status:', error);
    }
  };

  const fetchRouters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/mikrotik/routers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRouters(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching routers:', error);
    }
  };

  // ========== ROUTE MANAGEMENT FUNCTIONS ==========
  
  const fetchRoutes = async () => {
    setLoadingRoutes(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/l2tp/routes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRoutes(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching routes:', error);
      toast.error('Gagal mengambil daftar route');
    } finally {
      setLoadingRoutes(false);
    }
  };

  const fetchInterfaces = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/l2tp/interfaces`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInterfaces(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching interfaces:', error);
    }
  };

  const handleAddRoute = async () => {
    if (!newRoute.destination) {
      toast.error('Destination network wajib diisi');
      return;
    }

    if (routeType === 'static' && !newRoute.gateway && !newRoute.interface) {
      toast.error('Gateway atau interface wajib diisi');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let endpoint = `${API_URL}/api/v2/l2tp/routes`;
      let body = {};

      if (routeType === 'static') {
        body = {
          destination: newRoute.destination,
          gateway: newRoute.gateway || null,
          interface: newRoute.interface || null,
          metric: newRoute.metric ? parseInt(newRoute.metric) : null,
          persistent: newRoute.persistent
        };
      } else {
        // Multipath route
        endpoint = `${API_URL}/api/v2/l2tp/routes/multipath`;
        const validNexthops = multipathNexthops.filter(nh => nh.dev);
        if (validNexthops.length < 2) {
          toast.error('Minimal 2 interface untuk multipath route');
          return;
        }
        body = {
          destination: newRoute.destination,
          nexthops: validNexthops,
          persistent: newRoute.persistent
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Route berhasil ditambahkan');
        setShowAddRouteModal(false);
        setNewRoute({ destination: '', gateway: '', interface: '', metric: '', persistent: true });
        setMultipathNexthops([{ dev: '', weight: 1 }, { dev: '', weight: 1 }]);
        fetchRoutes();
      } else {
        toast.error(data.detail || 'Gagal menambah route');
      }
    } catch (error) {
      console.error('Error adding route:', error);
      toast.error('Gagal menambah route');
    }
  };

  const handleDeleteRoute = async (destination) => {
    if (!confirm(`Hapus route ${destination}?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/l2tp/routes/${encodeURIComponent(destination)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Route berhasil dihapus');
        fetchRoutes();
      } else {
        toast.error(data.detail || 'Gagal menghapus route');
      }
    } catch (error) {
      console.error('Error deleting route:', error);
      toast.error('Gagal menghapus route');
    }
  };

  const addNexthop = () => {
    setMultipathNexthops([...multipathNexthops, { dev: '', weight: 1 }]);
  };

  const removeNexthop = (index) => {
    if (multipathNexthops.length > 2) {
      setMultipathNexthops(multipathNexthops.filter((_, i) => i !== index));
    }
  };

  const updateNexthop = (index, field, value) => {
    const updated = [...multipathNexthops];
    updated[index][field] = field === 'weight' ? parseInt(value) || 1 : value;
    setMultipathNexthops(updated);
  };

  // ========== END ROUTE MANAGEMENT ==========

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.password) {
      toast.error('Username dan password wajib diisi');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/l2tp/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUser)
      });

      const data = await response.json();
      
      if (response.ok || data.success) {
        toast.success(data.message || 'User berhasil ditambahkan');
        if (data.mikrotik_result) {
          if (data.mikrotik_result.success) {
            toast.success(data.mikrotik_result.message);
          } else {
            toast.warning(`MikroTik: ${data.mikrotik_result.message}`);
          }
        }
        setShowAddModal(false);
        setNewUser({ username: '', password: '', ip_address: '*', description: '', router_id: '' });
        fetchUsers();
      } else {
        toast.error(data.detail || data.message || 'Gagal menambahkan user');
      }
    } catch (error) {
      console.error('Error adding user:', error);
      // Check if it's a network error or parsing error
      if (error.name === 'SyntaxError') {
        // Response might not be JSON, could still be successful
        toast.success('User mungkin berhasil ditambahkan, silakan refresh');
        setShowAddModal(false);
        setNewUser({ username: '', password: '', ip_address: '*', description: '', router_id: '' });
        fetchUsers();
      } else {
        toast.error('Gagal menambahkan user: ' + (error.message || 'Unknown error'));
      }
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Hapus user ${username}?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/l2tp/users/${username}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`User ${username} berhasil dihapus`);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Gagal menghapus user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Gagal menghapus user');
    }
  };

  const handleDisconnect = async (username) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/l2tp/sessions/${username}/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`Session ${username} disconnected`);
        fetchSessions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Gagal disconnect session');
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Gagal disconnect session');
    }
  };

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">L2TP Management</h1>
          <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">Kelola VPN L2TP untuk Remote ONT & POP</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { fetchData(); fetchRoutes(); fetchInterfaces(); }} className="bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 shadow-sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Server Status Card */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 rounded-xl sm:rounded-2xl p-3 sm:p-6 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="bg-white/20 backdrop-blur p-2 sm:p-4 rounded-lg sm:rounded-xl">
              <Server className="w-5 h-5 sm:w-8 sm:h-8 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm sm:text-xl">L2TP Server</h3>
              <p className="text-indigo-200 text-xs sm:text-sm">103.179.252.150</p>
            </div>
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-8">
            <div className="text-center">
              <p className="text-2xl sm:text-4xl font-bold text-white">{sessions.length}</p>
              <p className="text-xs text-indigo-200">Active</p>
            </div>
            <Badge className="bg-emerald-400 hover:bg-emerald-400 text-white px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-semibold shadow-lg">
              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Running
            </Badge>
          </div>
        </div>
      </div>

      {/* Tabs for Users, Routes and POP Management */}
      <Tabs defaultValue="pop" className="w-full" onValueChange={(v) => { if(v === 'routes') { fetchRoutes(); fetchInterfaces(); } }}>
        <TabsList className="bg-white/80 dark:bg-slate-800/80 backdrop-blur p-1 sm:p-2 rounded-xl sm:rounded-2xl mb-4 sm:mb-5 shadow-lg border border-slate-200 dark:border-slate-600/50 dark:border-slate-700/50 flex overflow-x-auto w-full">
          <TabsTrigger value="pop" className="flex-1 min-w-fit gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-purple-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg sm:rounded-xl px-2 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold transition-all">
            <Router className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">POP Management</span>
            <span className="sm:hidden">POP</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="flex-1 min-w-fit gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-purple-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg sm:rounded-xl px-2 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold transition-all">
            <Users className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">L2TP Users</span>
            <span className="sm:hidden">Users</span>
          </TabsTrigger>
          <TabsTrigger value="routes" className="flex-1 min-w-fit gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-purple-500 data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg sm:rounded-xl px-2 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold transition-all">
            <Network className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">IP Routes</span>
            <span className="sm:hidden">Routes</span>
          </TabsTrigger>
        </TabsList>

        {/* Users Tab Content */}
        <TabsContent value="users" className="space-y-4">
          {/* Add User Button */}
          <div className="flex justify-end">
            <Button onClick={() => setShowAddModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Tambah User
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-2 sm:gap-5">
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-3 sm:p-6 rounded-xl sm:rounded-2xl shadow-xl">
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-4">
                <div className="bg-white/20 backdrop-blur p-2 sm:p-4 rounded-lg sm:rounded-xl">
                  <Users className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-xl sm:text-4xl font-bold text-white">{users.length}</p>
                  <p className="text-xs sm:text-sm text-blue-100 font-medium">Total Users</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-3 sm:p-6 rounded-xl sm:rounded-2xl shadow-xl">
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-4">
                <div className="bg-white/20 backdrop-blur p-2 sm:p-4 rounded-lg sm:rounded-xl">
                  <Wifi className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-xl sm:text-4xl font-bold text-white">{sessions.length}</p>
                  <p className="text-xs sm:text-sm text-emerald-100 font-medium">Online</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-rose-600 p-3 sm:p-6 rounded-xl sm:rounded-2xl shadow-xl">
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-4">
                <div className="bg-white/20 backdrop-blur p-2 sm:p-4 rounded-lg sm:rounded-xl">
                  <Power className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-xl sm:text-4xl font-bold text-white">{users.length - sessions.length}</p>
                  <p className="text-xs sm:text-sm text-orange-100 font-medium">Offline</p>
                </div>
              </div>
            </div>
          </div>

          {/* Active Sessions */}
          {sessions.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border shadow-sm">
              <div className="p-4 border-b bg-gradient-to-r from-emerald-50 to-white dark:from-emerald-900/30 dark:to-slate-800">
                <h3 className="font-semibold flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                  <Wifi className="w-5 h-5" />
                  Active Sessions ({sessions.length})
                </h3>
              </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left">Interface</th>
                  <th className="px-4 py-3 text-left">Username</th>
                  <th className="px-4 py-3 text-left">IP Address</th>
                  <th className="px-4 py-3 text-left">Calling SID</th>
                  <th className="px-4 py-3 text-left">Uptime</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session, idx) => (
                  <tr key={idx} className="border-b dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 dark:hover:bg-slate-700 dark:bg-slate-700">
                    <td className="px-4 py-3 font-mono text-xs">{session.interface}</td>
                    <td className="px-4 py-3 font-medium">{session.username}</td>
                    <td className="px-4 py-3 font-mono text-xs">{session.ip_address}</td>
                    <td className="px-4 py-3 font-mono text-xs">{session.calling_sid}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="text-green-600 dark:text-green-400">
                        <Clock className="w-3 h-3 mr-1" />
                        {session.uptime}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 dark:text-red-400"
                        onClick={() => handleDisconnect(session.username)}
                      >
                        <Power className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

          {/* Users List */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 overflow-hidden dark:border-slate-700">
            <div className="p-5 border-b bg-gradient-to-r from-indigo-500 to-purple-500">
              <h3 className="font-bold flex items-center gap-2 text-white text-lg">
                <Users className="w-5 h-5" />
                L2TP Users ({users.length})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 dark:bg-slate-700">
                  <tr>
                    <th className="px-4 py-4 text-left font-bold text-slate-700 dark:text-slate-200">Status</th>
                    <th className="px-4 py-4 text-left font-bold text-slate-700 dark:text-slate-200">Username</th>
                    <th className="px-4 py-4 text-left font-bold text-slate-700 dark:text-slate-200">Password</th>
                    <th className="px-4 py-4 text-left font-bold text-slate-700 dark:text-slate-200">IP Address</th>
                    <th className="px-4 py-4 text-left font-bold text-slate-700 dark:text-slate-200">Session Info</th>
                    <th className="px-4 py-4 text-left font-bold text-slate-700 dark:text-slate-200">Router</th>
                    <th className="px-4 py-4 text-left font-bold text-slate-700 dark:text-slate-200">Description</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, idx) => {
                    // Status now comes directly from user object (from backend)
                    const isOnline = user.is_online === true;
                    
                    return (
                      <tr key={idx} className={`border-b dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-blue-900/30/50 transition-colors ${isOnline ? 'bg-emerald-50/30' : ''}`}>
                        <td className="px-4 py-3">
                          {isOnline ? (
                            <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white shadow-sm">
                              <CheckCircle className="w-3 h-3 mr-1" /> Online
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-400 border-slate-300 dark:text-slate-500 dark:border-slate-600">
                              <XCircle className="w-3 h-3 mr-1" /> Offline
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200">{user.username}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-xs">
                              {showPasswords[user.username] ? user.password : '••••••••'}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 hover:bg-slate-200"
                              onClick={() => togglePassword(user.username)}
                            >
                              {showPasswords[user.username] ? (
                                <EyeOff className="w-3 h-3 text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400" />
                              ) : (
                                <Eye className="w-3 h-3 text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400" />
                              )}
                            </Button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-xs">{user.ip_address}</code>
                        </td>
                        <td className="px-4 py-3">
                          {isOnline ? (
                            <div className="text-xs space-y-1">
                              <div className="flex items-center gap-1">
                                <span className="text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">IP:</span>
                                <code className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded dark:bg-emerald-900/40 dark:text-emerald-300">{user.session_ip || user.ip_address}</code>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">From:</span>
                                <span className="font-mono text-slate-600 dark:text-slate-300">{user.interface || "-"}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-emerald-500" />
                                <span className="text-emerald-600 font-medium dark:text-emerald-400">{user.uptime || "Connected"}</span>
                              </div>
                              {(user.bytes_sent > 0 || user.bytes_rcvd > 0) && (
                                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mt-1">
                                  <span className="text-blue-500">↑ {formatBytes(user.bytes_sent)}</span>
                                  <span className="text-green-500">↓ {formatBytes(user.bytes_rcvd)}</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{user.router_name || '-'}</td>
                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">{user.description || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {isOnline && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-orange-600 hover:text-orange-700 dark:text-orange-400"
                            onClick={() => handleDisconnect(user.username)}
                            title="Disconnect session"
                          >
                            <Power className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 dark:text-red-400"
                          onClick={() => handleDeleteUser(user.username)}
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">
                    Belum ada user L2TP
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Tambah User L2TP</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Username *</label>
                <Input
                  placeholder="mikrotik-cabang1"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Password *</label>
                <Input
                  type="password"
                  placeholder="Password L2TP"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">IP Address (opsional)</label>
                <Input
                  placeholder="* (dynamic) atau 10.255.255.10"
                  value={newUser.ip_address}
                  onChange={(e) => setNewUser({...newUser, ip_address: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Gunakan * untuk IP dynamic</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Router (auto-create L2TP client)</label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  value={newUser.router_id}
                  onChange={(e) => setNewUser({...newUser, router_id: e.target.value})}
                >
                  <option value="">-- Tidak auto-create --</option>
                  {routers.map(router => (
                    <option key={router.id} value={router.id}>{router.name}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Pilih router untuk auto-create L2TP client di MikroTik</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Description</label>
                <Input
                  placeholder="Cabang Depok"
                  value={newUser.description}
                  onChange={(e) => setNewUser({...newUser, description: e.target.value})}
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>
                Batal
              </Button>
              <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={handleAddUser}>
                <Plus className="w-4 h-4 mr-2" />
                Tambah
              </Button>
            </div>
          </div>
        </div>
      )}
        </TabsContent>

        {/* Routes Tab Content */}
        <TabsContent value="routes" className="space-y-4">
          {/* Add Route Button */}
          <div className="flex justify-end">
            <Button onClick={() => { setShowAddRouteModal(true); fetchInterfaces(); }} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Tambah Route
            </Button>
          </div>

          {/* Routes Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border">
              <div className="flex items-center gap-3">
                <Network className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                <div>
                  <p className="text-2xl font-bold">{routes.length}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">Total Routes</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border">
              <div className="flex items-center gap-3">
                <Layers className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                <div>
                  <p className="text-2xl font-bold">{routes.filter(r => r.type === 'multipath').length}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">Multipath Routes</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border">
              <div className="flex items-center gap-3">
                <Globe className="w-8 h-8 text-green-600 dark:text-green-400" />
                <div>
                  <p className="text-2xl font-bold">{interfaces.filter(i => i.state === 'UP').length}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">Active Interfaces</p>
                </div>
              </div>
            </div>
          </div>

          {/* Interfaces List */}
          {interfaces.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-lg border p-4">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <Wifi className="w-4 h-4" /> Available Interfaces
              </h4>
              <div className="flex flex-wrap gap-2">
                {interfaces.map((iface, idx) => (
                  <Badge key={idx} variant={iface.state === 'UP' ? 'default' : 'secondary'}>
                    {iface.name} ({iface.state})
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Routes Table */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-semibold flex items-center gap-2">
                <Network className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                IP Routes
              </h3>
              <Button onClick={fetchRoutes} variant="ghost" size="sm" disabled={loadingRoutes}>
                <RefreshCw className={`w-4 h-4 ${loadingRoutes ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left">Destination</th>
                    <th className="px-4 py-3 text-left">Gateway/Interface</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Metric</th>
                    <th className="px-4 py-3 text-left">Persistent</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {routes.map((route, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700 dark:hover:bg-slate-700 dark:bg-slate-700">
                      <td className="px-4 py-3 font-mono text-sm">{route.destination}</td>
                      <td className="px-4 py-3">
                        {route.type === 'multipath' ? (
                          <div className="space-y-1">
                            {route.nexthops.map((nh, nhIdx) => (
                              <div key={nhIdx} className="flex items-center gap-1 text-xs">
                                <ArrowRight className="w-3 h-3 text-slate-400 dark:text-slate-500 dark:text-slate-400" />
                                <span>{nh.via ? `via ${nh.via}` : ''} {nh.dev ? `dev ${nh.dev}` : ''}</span>
                                {nh.weight && <Badge variant="outline" className="text-xs">w:{nh.weight}</Badge>}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm">
                            {route.gateway ? `via ${route.gateway}` : ''} 
                            {route.interface ? `dev ${route.interface}` : ''}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={route.type === 'multipath' ? 'default' : 'secondary'}>
                          {route.type === 'multipath' ? 'Multipath' : 'Static'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">{route.metric || '-'}</td>
                      <td className="px-4 py-3">
                        {route.persistent ? (
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-slate-300" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {!route.destination.includes('default') && !route.scope && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                            onClick={() => handleDeleteRoute(route.destination)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {routes.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">
                        {loadingRoutes ? 'Memuat routes...' : 'Tidak ada route'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* POP Management Tab Content */}
        <TabsContent value="pop" className="space-y-4">
          <POPManagement />
        </TabsContent>
      </Tabs>

      {/* Add Route Modal */}
      {showAddRouteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Tambah IP Route</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAddRouteModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              {/* Route Type Selection */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block dark:text-slate-200">Tipe Route</label>
                <div className="flex gap-2">
                  <Button
                    variant={routeType === 'static' ? 'default' : 'outline'}
                    className={routeType === 'static' ? 'bg-indigo-600' : ''}
                    onClick={() => setRouteType('static')}
                  >
                    <Route className="w-4 h-4 mr-2" />
                    Static Route
                  </Button>
                  <Button
                    variant={routeType === 'multipath' ? 'default' : 'outline'}
                    className={routeType === 'multipath' ? 'bg-indigo-600' : ''}
                    onClick={() => setRouteType('multipath')}
                  >
                    <Layers className="w-4 h-4 mr-2" />
                    Multipath (Load Balance)
                  </Button>
                </div>
              </div>

              {/* Destination Network */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Destination Network *</label>
                <Input
                  placeholder="172.16.11.0/24"
                  value={newRoute.destination}
                  onChange={(e) => setNewRoute({...newRoute, destination: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Format: network/prefix (contoh: 192.168.1.0/24)</p>
              </div>

              {routeType === 'static' ? (
                <>
                  {/* Gateway */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Gateway (opsional)</label>
                    <Input
                      placeholder="10.255.255.1"
                      value={newRoute.gateway}
                      onChange={(e) => setNewRoute({...newRoute, gateway: e.target.value})}
                    />
                  </div>

                  {/* Interface */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Interface (opsional)</label>
                    <Select
                      value={newRoute.interface}
                      onValueChange={(value) => setNewRoute({...newRoute, interface: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pilih interface..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">-- Tidak menggunakan interface --</SelectItem>
                        {interfaces.map((iface, idx) => (
                          <SelectItem key={idx} value={iface.name}>
                            {iface.name} ({iface.state})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Metric */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Metric (opsional)</label>
                    <Input
                      type="number"
                      placeholder="100"
                      value={newRoute.metric}
                      onChange={(e) => setNewRoute({...newRoute, metric: e.target.value})}
                    />
                  </div>
                </>
              ) : (
                /* Multipath Nexthops */
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block dark:text-slate-200">Nexthops (min. 2)</label>
                  <div className="space-y-2">
                    {multipathNexthops.map((nh, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <Select
                          value={nh.dev}
                          onValueChange={(value) => updateNexthop(idx, 'dev', value)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Pilih interface..." />
                          </SelectTrigger>
                          <SelectContent>
                            {interfaces.filter(i => i.name.startsWith('ppp')).map((iface, i) => (
                              <SelectItem key={i} value={iface.name}>
                                {iface.name} ({iface.state})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          placeholder="Weight"
                          className="w-24"
                          value={nh.weight}
                          onChange={(e) => updateNexthop(idx, 'weight', e.target.value)}
                        />
                        {multipathNexthops.length > 2 && (
                          <Button variant="ghost" size="sm" onClick={() => removeNexthop(idx)}>
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addNexthop} className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Tambah Nexthop
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 dark:text-slate-400">
                    Multipath route akan membagi traffic ke semua interface (load balancing)
                  </p>
                </div>
              )}

              {/* Persistent Toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="persistent"
                  checked={newRoute.persistent}
                  onChange={(e) => setNewRoute({...newRoute, persistent: e.target.checked})}
                  className="rounded"
                />
                <label htmlFor="persistent" className="text-sm text-slate-700 dark:text-slate-200">
                  Persistent (tetap ada setelah reboot)
                </label>
              </div>

              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/30">
                <p className="text-xs text-blue-800">
                  <strong>Info:</strong> Route akan ditambahkan ke server ACS ({serverStatus?.host}).
                  {newRoute.persistent && ' Route juga akan disimpan di /etc/ppp/ip-up.local agar tetap ada setelah reboot.'}
                </p>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setShowAddRouteModal(false)}>
                Batal
              </Button>
              <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={handleAddRoute}>
                <Plus className="w-4 h-4 mr-2" />
                Tambah Route
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default L2TPManagement;
